/**
 * Image compression utilities for reducing file size before upload.
 * Uses native Canvas API - no external dependencies required.
 */

export interface CompressionOptions {
  /** Maximum width in pixels (default: 1920) */
  maxWidth?: number;
  /** Maximum height in pixels (default: 1920) */
  maxHeight?: number;
  /** JPEG quality 0-1 (default: 0.8) */
  quality?: number;
  /** Maximum file size in bytes. If exceeded, quality will be reduced (default: 1MB) */
  maxSizeBytes?: number;
}

const DEFAULT_OPTIONS: Required<CompressionOptions> = {
  maxWidth: 1920,
  maxHeight: 1920,
  quality: 0.8,
  maxSizeBytes: 1024 * 1024, // 1MB
};

/**
 * Compresses an image file by resizing and reducing quality.
 * Returns the original file if compression fails or isn't beneficial.
 */
export async function compressImage(
  file: File,
  options: CompressionOptions = {}
): Promise<File> {
  // Skip non-image files
  if (!file.type.startsWith("image/")) {
    return file;
  }

  // Skip already small files (< 500KB)
  if (file.size < 500 * 1024) {
    return file;
  }

  // Skip formats that don't compress well or aren't supported by canvas
  const skipFormats = ["image/gif", "image/svg+xml"];
  if (skipFormats.includes(file.type)) {
    return file;
  }

  const opts = { ...DEFAULT_OPTIONS, ...options };

  try {
    const compressedFile = await compressImageInternal(file, opts);

    // Only use compressed version if it's actually smaller
    if (compressedFile.size < file.size) {
      console.log(
        `[ImageCompression] ${file.name}: ${formatSize(
          file.size
        )} -> ${formatSize(compressedFile.size)} (${Math.round(
          (1 - compressedFile.size / file.size) * 100
        )}% reduction)`
      );
      return compressedFile;
    } else {
      console.log(
        `[ImageCompression] ${file.name}: compression not beneficial, keeping original`
      );
      return file;
    }
  } catch (error) {
    console.warn(`[ImageCompression] Failed to compress ${file.name}:`, error);
    return file;
  }
}

/**
 * Compresses multiple images in parallel with concurrency limit.
 */
export async function compressImages(
  files: File[],
  options: CompressionOptions = {},
  onProgress?: (completed: number, total: number) => void
): Promise<File[]> {
  const concurrency = 3;
  const results: File[] = new Array(files.length);
  let completed = 0;

  const processFile = async (index: number): Promise<void> => {
    results[index] = await compressImage(files[index], options);
    completed++;
    onProgress?.(completed, files.length);
  };

  // Process in batches
  for (let i = 0; i < files.length; i += concurrency) {
    const batch = files
      .slice(i, i + concurrency)
      .map((_, idx) => processFile(i + idx));
    await Promise.all(batch);
  }

  return results;
}

async function compressImageInternal(
  file: File,
  opts: Required<CompressionOptions>
): Promise<File> {
  // Load image
  const img = await loadImage(file);

  // Calculate new dimensions
  let { width, height } = img;
  const aspectRatio = width / height;

  if (width > opts.maxWidth) {
    width = opts.maxWidth;
    height = Math.round(width / aspectRatio);
  }

  if (height > opts.maxHeight) {
    height = opts.maxHeight;
    width = Math.round(height * aspectRatio);
  }

  // Create canvas and draw resized image
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;

  const ctx = canvas.getContext("2d");
  if (!ctx) {
    throw new Error("Could not get canvas context");
  }

  // Use better quality scaling
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(img, 0, 0, width, height);

  // Convert to blob with initial quality
  let quality = opts.quality;
  let blob = await canvasToBlob(canvas, "image/jpeg", quality);

  // If still too large, reduce quality progressively
  while (blob.size > opts.maxSizeBytes && quality > 0.3) {
    quality -= 0.1;
    blob = await canvasToBlob(canvas, "image/jpeg", quality);
  }

  // Create new file with same name but .jpg extension
  const newName = file.name.replace(/\.[^.]+$/, ".jpg");
  return new File([blob], newName, {
    type: "image/jpeg",
    lastModified: file.lastModified,
  });
}

function loadImage(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(img.src);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(img.src);
      reject(new Error("Failed to load image"));
    };
    img.src = URL.createObjectURL(file);
  });
}

function canvasToBlob(
  canvas: HTMLCanvasElement,
  type: string,
  quality: number
): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) {
          resolve(blob);
        } else {
          reject(new Error("Failed to create blob"));
        }
      },
      type,
      quality
    );
  });
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}
